(() => {
  const roofConfigurationHandle = 'lc300-roof-system-configuration-internal';
  const gorillaMountHandle = 'starlink-mini-mount';
  const hostSelector = '[data-gm-lc300-options-host]';
  const fieldsSelector = '[data-gm-lc300-options-fields]';
  const expectedFinishCount = 11;
  const expectedMountColourCount = 5;

  const findComponentItem = (productInfo, handle) => {
    const matchingLink = Array.from(
      productInfo.querySelectorAll('.rbr-sbap_item a[href]')
    ).find((link) => link.getAttribute('href')?.includes(`/products/${handle}`));

    return matchingLink?.closest('.rbr-sbap_item') || null;
  };

  const getSourceSelect = (item) => item?.querySelector('.rbr-sbap_variants select') || null;

  const getVariantOptions = (select) =>
    Array.from(select?.options || []).filter(
      (option) => option.value && option.textContent.trim()
    );

  const getSourceWrapper = (select) =>
    select?.closest('.rbr-sbap_variants') || select?.parentElement || null;

  const showSource = (select) => {
    const wrapper = getSourceWrapper(select);
    wrapper?.classList.remove('gm-lc300-fastbundle-options__source');
    wrapper?.removeAttribute('aria-hidden');
  };

  const hideSource = (select) => {
    const wrapper = getSourceWrapper(select);
    wrapper?.classList.add('gm-lc300-fastbundle-options__source');
    wrapper?.setAttribute('aria-hidden', 'true');
  };

  const revealNativeSelectors = (host, sourceSelects = []) => {
    sourceSelects.filter(Boolean).forEach(showSource);

    const fields = host.querySelector(fieldsSelector);
    fields?.replaceChildren();
    if (fields) fields.hidden = true;

    host.sourceSignature = '';
    host.sourceSelects = [];
    host.hidden = false;
  };

  const setLinkFromHostData = (host, selector, dataKey) => {
    const link = host.querySelector(selector);
    const destination = host.dataset[dataKey];
    if (link && destination) link.href = destination;
  };

  const placeAfterBundle = (host, bundleRoot) => {
    if (host.previousElementSibling !== bundleRoot) {
      bundleRoot.insertAdjacentElement('afterend', host);
    }
  };

  const setNativeSelectValue = (select, value) => {
    if (!select || !value || select.value === value) return;

    const nativeValueSetter = Object.getOwnPropertyDescriptor(
      HTMLSelectElement.prototype,
      'value'
    )?.set;

    if (nativeValueSetter) {
      nativeValueSetter.call(select, value);
    } else {
      select.value = value;
    }

    select.dispatchEvent(new Event('input', { bubbles: true }));
    select.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const createField = ({ id, label, sourceSelect, surchargeLabel }) => {
    const field = document.createElement('div');
    field.className = 'gm-lc300-fastbundle-options__field';

    const labelElement = document.createElement('label');
    labelElement.className = 'gm-lc300-fastbundle-options__label';
    labelElement.htmlFor = id;
    labelElement.textContent = label;

    const select = document.createElement('select');
    select.id = id;
    select.className = 'gm-lc300-fastbundle-options__select';
    select.setAttribute('aria-label', label);

    getVariantOptions(sourceSelect).forEach((sourceOption) => {
      const option = document.createElement('option');
      const title = sourceOption.textContent.trim();
      option.value = sourceOption.value;
      option.textContent = `${title}${surchargeLabel(title)}`;
      option.disabled = sourceOption.disabled;
      option.selected = sourceOption.selected;
      select.append(option);
    });

    if (!select.value) {
      const firstAvailable = Array.from(select.options).find((option) => !option.disabled);
      if (firstAvailable) select.value = firstAvailable.value;
    }

    const syncFromSource = () => {
      const matchingOption = Array.from(select.options).find(
        (option) => option.value === sourceSelect.value
      );
      if (matchingOption) {
        select.value = sourceSelect.value;
      }

      const sourceOptionsByValue = new Map(
        getVariantOptions(sourceSelect).map((option) => [option.value, option])
      );
      Array.from(select.options).forEach((option) => {
        option.disabled = sourceOptionsByValue.get(option.value)?.disabled || false;
      });
    };

    select.addEventListener('change', () => setNativeSelectValue(sourceSelect, select.value));
    sourceSelect.addEventListener('change', syncFromSource);
    field.append(labelElement, select);

    return field;
  };

  const getSourceSignature = (mode, selects) =>
    [mode]
      .concat(
        selects.flatMap((select) =>
          getVariantOptions(select).map(
            (option) =>
              `${option.value}:${option.textContent.trim()}:${option.disabled ? '0' : '1'}`
          )
        )
      )
      .join('|');

  const enhanceHost = (host) => {
    const productInfo = host.closest('product-info');
    const fields = host.querySelector(fieldsSelector);
    const mode = host.dataset.offerMode;
    if (!productInfo || !fields || !['with-mount', 'no-mount'].includes(mode)) return;

    setLinkFromHostData(host, '[data-gm-lc300-main-offer-link]', 'mainOfferUrl');
    setLinkFromHostData(host, '[data-gm-lc300-no-mount-offer-link]', 'noMountOfferUrl');

    const roofItem = findComponentItem(productInfo, roofConfigurationHandle);
    const roofSelect = getSourceSelect(roofItem);
    const mountItem = mode === 'with-mount' ? findComponentItem(productInfo, gorillaMountHandle) : null;
    const mountSelect = mode === 'with-mount' ? getSourceSelect(mountItem) : null;
    const bundleRoot = roofItem?.closest('#rbr-container-element-true');

    if (!roofSelect || !bundleRoot || (mode === 'with-mount' && !mountSelect)) return;

    const sourceSelects = [roofSelect, mountSelect].filter(Boolean);
    const finishOptions = getVariantOptions(roofSelect);
    const mountColourOptions = getVariantOptions(mountSelect);
    const hasExpectedOptions =
      finishOptions.length === expectedFinishCount &&
      (mode === 'no-mount' || mountColourOptions.length === expectedMountColourCount);

    if (!hasExpectedOptions) {
      revealNativeSelectors(host, sourceSelects);
      placeAfterBundle(host, bundleRoot);
      return;
    }

    const sourceSignature = getSourceSignature(mode, sourceSelects);
    const sourcesUnchanged =
      host.sourceSignature === sourceSignature &&
      host.sourceSelects?.length === sourceSelects.length &&
      host.sourceSelects.every((select, index) => select === sourceSelects[index]);

    sourceSelects.forEach(hideSource);

    if (!sourcesUnchanged) {
      const productId = host.dataset.productId || 'lc300';
      const finishField = createField({
        id: `GMLC300RoofPlateFinish-${productId}`,
        label: 'LC300 roof-plate finish',
        sourceSelect: roofSelect,
        surchargeLabel: (title) =>
          title.toLowerCase() === 'plain billet' ? '' : ' (+ $150)',
      });

      const fieldElements = [];
      if (mode === 'with-mount') {
        fieldElements.push(
          createField({
            id: `GMLC300MountColour-${productId}`,
            label: 'Gorilla Mount colour',
            sourceSelect: mountSelect,
            surchargeLabel: () => '',
          })
        );
      }
      fieldElements.push(finishField);

      fields.replaceChildren(...fieldElements);
      fields.hidden = false;
      host.sourceSignature = sourceSignature;
      host.sourceSelects = sourceSelects;
    }

    host.hidden = false;
    placeAfterBundle(host, bundleRoot);
  };

  const enhanceBundleOptions = () => {
    document.querySelectorAll(hostSelector).forEach(enhanceHost);
  };

  let enhancementScheduled = false;
  const scheduleEnhancement = () => {
    if (enhancementScheduled) return;
    enhancementScheduled = true;

    window.requestAnimationFrame(() => {
      enhancementScheduled = false;
      enhanceBundleOptions();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleEnhancement, { once: true });
  } else {
    scheduleEnhancement();
  }

  new MutationObserver(scheduleEnhancement).observe(document.body, {
    childList: true,
    subtree: true,
  });
})();
